import React, { useEffect, useMemo, useState } from 'react'
import { DevelopmentMediacore, RealEstateDataPageComponentData, StatePropertiesSlice } from 'types'
import { Container } from 'react-bootstrap'
import useQueryDevelopments from '@hooks/useQueryDevelopments'
import { excludeIncludeDevelopments } from '@helpers/helper.developments'
import { changeViewId } from '@slices/propertiesSlice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@redux/store'
import {
  setConstructionStatusFilter,
  setLocationFilter,
  setServicesFilter,
} from '@slices/developmentsSlice'
import Filters_5 from '../5'

interface DevelopmentsFilterProps extends RealEstateDataPageComponentData {
  tag?: string
}

const DevelopmentsFilter: React.FC<DevelopmentsFilterProps> = (props) => {
  const { global_data, excludeTag, hidden, filtersBarElements, includeTag } = props
  const dispatch = useDispatch()

  useEffect(() => {
    setIsMounted(true)
  }, [])
  const [menu, setMenu] = useState(false)

  const { data: allDevelopmentsData, isLoading } = useQueryDevelopments(
    global_data.pageConfig.keys.tokko,
  )
  const { viewId } = useSelector((state: { properties: StatePropertiesSlice }) => state.properties)

  const constructionStatusFilter = useSelector(
    (state: RootState) => state.developments.constructionStatus,
  )

  const locationFilter = useSelector((state: RootState) => state.developments.location)

  const servicesFilter = useSelector((state: RootState) => state.developments.services)

  const convertStatusIdToString = (statusId: number | string): string => {
    const statusMap: { [key: string]: string } = {
      '1': 'DESCONOCIDO',
      '2': 'REUNIENDO INVERSORES',
      '3': 'EN POZO',
      '4': 'EN CONSTRUCCIÓN',
      '5': 'DETENIDO',
      '6': 'TERMINADO',
    }
    return statusMap[statusId.toString()] || statusId.toString().toUpperCase()
  }

  const filteredDevelopments: DevelopmentMediacore[] = useMemo(() => {
    if (!allDevelopmentsData) return []
    return excludeIncludeDevelopments(allDevelopmentsData, [], excludeTag!)
  }, [allDevelopmentsData, excludeTag])

  const constructionStatusButtons = useMemo(() => {
    const statusSet = new Set(['VER TODOS'])
    return [
      { name: 'VER TODOS', value: 0 },
      ...filteredDevelopments.reduce(
        (buttons, dev) => {
          const status = convertStatusIdToString(dev.construction_status)
          if (!statusSet.has(status)) {
            statusSet.add(status)
            buttons.push({ name: status, value: buttons.length + 1 })
          }
          return buttons
        },
        [] as { name: string; value: number }[],
      ),
    ]
  }, [filteredDevelopments])

  const uniqueLocations = useMemo(() => {
    return [
      ...new Set(
        filteredDevelopments
          .filter(
            (dev) =>
              constructionStatusFilter === 'VER TODOS' ||
              constructionStatusFilter === '' ||
              convertStatusIdToString(dev.construction_status) === constructionStatusFilter,
          )
          .map((dev) => dev.location.name),
      ),
    ]
  }, [filteredDevelopments, constructionStatusFilter])

  const uniqueExtraServices = useMemo(() => {
    return [
      ...new Set(
        filteredDevelopments
          .filter((dev) => {
            const statusMatch =
              constructionStatusFilter === 'VER TODOS' ||
              constructionStatusFilter === '' ||
              convertStatusIdToString(dev.construction_status) === constructionStatusFilter

            const locationMatch = !locationFilter
              ? true
              : dev?.location?.name?.toLowerCase().includes(locationFilter.toLowerCase())

            return statusMatch && locationMatch
          })
          .flatMap((dev) => dev.extra_services ?? [])
          .filter((service) => includeTag?.includes(service)),
      ),
    ]
  }, [filteredDevelopments, constructionStatusFilter, locationFilter])

  const cleanFilters = () => {
    dispatch(setLocationFilter(''))
    dispatch(setConstructionStatusFilter(''))
    dispatch(setServicesFilter(''))
  }

  const [isMounted, setIsMounted] = useState(false)

  const servicesFilterElement = filtersBarElements?.find((element) => element.filter === 'services')

  const locationFilterElement = filtersBarElements?.find((element) => element.filter === 'location')

  return (
    isMounted && (
      <div
        className={`development-filter handle-filter-bar a pt-3 background-tertiary`}
        id="handle-filter-bar"
      >
        <section>
          <div id="developments-filter">
            <Container fluid>
              <div className="d-flex flex-wrap justify-content-between align-items-center pb-3">
                <div className="d-none d-lg-flex">
                  {!isLoading && (
                    <>
                      <select
                        className={`custom-clean me-3 form-select dropdown-toggle`}
                        onChange={(e) => {
                          dispatch(setConstructionStatusFilter(e.target.value))
                        }}
                        value={constructionStatusFilter}
                      >
                        <option
                          value=""
                          disabled
                        >
                          Estado de construcción
                        </option>
                        {constructionStatusButtons.map((button, index) => (
                          <option
                            key={index}
                            value={button.name}
                          >
                            {button.name}
                          </option>
                        ))}
                      </select>
                      {locationFilterElement && (
                        <select
                          className="custom-clean form-select dropdown-toggle"
                          onChange={(e) => {
                            dispatch(setLocationFilter(e.target.value))
                          }}
                          value={locationFilter}
                        >
                          <option
                            value=""
                            disabled
                          >
                            {locationFilterElement.label ?? 'Ubicación'}
                          </option>
                          {uniqueLocations.map((location, index) => (
                            <option
                              key={index}
                              value={location}
                              className="text-start"
                            >
                              {location}
                            </option>
                          ))}
                        </select>
                      )}
                      {servicesFilterElement && (
                        <select
                          className="custom-clean form-select dropdown-toggle"
                          onChange={(e) => {
                            dispatch(setServicesFilter(e.target.value))
                          }}
                          value={servicesFilter}
                        >
                          <option
                            value=""
                            disabled
                          >
                            {servicesFilterElement.label ?? 'Servicios'}
                          </option>
                          {uniqueExtraServices.map((service, index) => (
                            <option
                              key={index}
                              value={service}
                              className="text-start"
                            >
                              {service}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        className="cursor-pointer custom-clean-button"
                        onClick={() => {
                          cleanFilters()
                        }}
                      >
                        Limpiar filtros
                      </button>
                    </>
                  )}
                </div>
                <div className="d-lg-none content-field cursor-pointer">
                  <div
                    className="dropdown-select btn btn-secondary white d-flex align-items-center custom-filter"
                    onClick={() => {
                      setMenu(true)
                    }}
                  >
                    <span className="text-white">Filtros de búsqueda</span>
                  </div>
                </div>
                <div className="handlers-items d-flex align-items-center">
                  <div
                    onClick={() => dispatch(changeViewId(0))}
                    className={
                      'btn button-item d-none d-lg-flex ' +
                      (viewId === 0 && 'active') +
                      (hidden ? ' order-3 order-lg-2' : ' order-3 order-lg-1')
                    }
                  >
                    <i className="icon-grid_location" />
                  </div>
                  <div
                    onClick={() => dispatch(changeViewId(1))}
                    className={
                      'btn button-item ' +
                      (viewId === 1 && 'active') +
                      (hidden ? ' order-lg-3 order-3' : ' order-lg-2 order-2')
                    }
                  >
                    {' '}
                    <i className="icon-location" />
                  </div>
                  <div
                    onClick={() => dispatch(changeViewId(2))}
                    className={
                      'btn button-item ' +
                      (viewId === 2 && 'active') +
                      (hidden ? ' order-lg-1 order-1' : ' order-lg-3 order-1')
                    }
                  >
                    {' '}
                    <i className="icon-grid-view" />
                  </div>
                  <Filters_5
                    menu={menu}
                    setMenu={setMenu}
                    global_data={global_data}
                    constructionStatusButtons={constructionStatusButtons}
                    locations={uniqueLocations ?? []}
                  />
                </div>
              </div>
            </Container>
          </div>
        </section>{' '}
        <div className="d-none d-lg-flex current-filters-sumary py-3">
          <div className="container-fluid">
            <span className="filters-span">Filtros aplicados: </span>
            <div className="">
              <div className="d-inline-block">
                {constructionStatusFilter && (
                  <div
                    className={
                      'tag tag-button d-inline-flex align-items-center justify-content-center '
                    }
                  >
                    {constructionStatusFilter}
                  </div>
                )}
                {locationFilter && (
                  <div
                    className={
                      'tag tag-button d-inline-flex align-items-center justify-content-center '
                    }
                  >
                    {locationFilter}
                  </div>
                )}
                {servicesFilter && (
                  <div
                    className={
                      'tag tag-button d-inline-flex align-items-center justify-content-center '
                    }
                  >
                    {servicesFilter}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  )
}

export default DevelopmentsFilter
